import { useState, useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

interface SubscribeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function SubscribeModal({ isOpen, onClose }: SubscribeModalProps) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [otherRole, setOtherRole] = useState('');
    const [interests, setInterests] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t, language } = useLanguage();

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    const handleInterestChange = (value: string) => {
        setInterests(prev =>
            prev.includes(value)
                ? prev.filter(i => i !== value)
                : [...prev, value]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            toast.error(language === 'zh' ? '请输入邮箱地址' : 'Please enter your email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error(language === 'zh' ? '请输入有效的邮箱地址' : 'Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    name: name || undefined,
                    role: role || undefined,
                    otherRole: role === 'other' ? otherRole : undefined,
                    interests: interests.length > 0 ? interests : undefined,
                })
            });
            const data = await response.json();

            if (data.success) {
                toast.success(t.footer.subscribeSuccess);
                setEmail('');
                setName('');
                setRole('');
                setOtherRole('');
                setInterests([]);
                onClose();
            } else {
                toast.error(t.footer.subscribeError);
            }
        } catch {
            toast.error(language === 'zh' ? '网络错误，请稍后重试' : 'Network error, please try again');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-md transition-all duration-300"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-[rgba(255,255,255,0.08)] shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a24] via-[#0f0f16] to-[#0a0a0f] opacity-90" />

                {/* Glow effects */}
                <div
                    className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-20 pointer-events-none blur-3xl"
                    style={{
                        background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)',
                        transform: 'translate(30%, -30%)',
                    }}
                />
                <div
                    className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full opacity-10 pointer-events-none blur-3xl"
                    style={{
                        background: 'radial-gradient(circle, rgba(6,182,212,0.3) 0%, transparent 70%)',
                        transform: 'translate(-30%, 30%)',
                    }}
                />

                {/* Close button */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.05)] text-[var(--ds-text-secondary)] hover:text-white hover:bg-[rgba(255,255,255,0.1)] transition-all cursor-pointer z-50"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Content */}
                <div className="relative z-10 p-8 sm:p-10">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                            {t.footer.subscribeTitle}
                        </h2>
                        <p className="text-sm text-[var(--ds-text-secondary)] opacity-80">
                            {language === 'zh'
                                ? '填写以下信息，第一时间获取产品更新'
                                : 'Fill in the form below to get the latest updates'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email Input */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-[var(--ds-text-secondary)] ml-1">
                                {language === 'zh' ? '邮箱' : 'Email'} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t.footer.subscribePlaceholder}
                                className="w-full h-12 px-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] text-[var(--ds-text-primary)] placeholder:text-[#64748b] text-sm focus:outline-none focus:border-[var(--ds-accent-purple)] focus:bg-[rgba(255,255,255,0.05)] focus:ring-1 focus:ring-[var(--ds-accent-purple)] transition-all"
                            />
                        </div>

                        {/* Name Input */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-[var(--ds-text-secondary)] ml-1">
                                {language === 'zh' ? '称呼' : 'Name'} <span className="text-[rgba(255,255,255,0.3)]">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t.footer.subscribeNamePlaceholder}
                                className="w-full h-12 px-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] text-[var(--ds-text-primary)] placeholder:text-[#64748b] text-sm focus:outline-none focus:border-[var(--ds-accent-purple)] focus:bg-[rgba(255,255,255,0.05)] focus:ring-1 focus:ring-[var(--ds-accent-purple)] transition-all"
                            />
                        </div>

                        {/* Role Select */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-[var(--ds-text-secondary)] ml-1">
                                {t.footer.subscribeRoleLabel}
                            </label>
                            <div className="relative">
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    className="w-full h-12 px-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] text-[var(--ds-text-primary)] text-sm focus:outline-none focus:border-[var(--ds-accent-purple)] focus:bg-[rgba(255,255,255,0.05)] focus:ring-1 focus:ring-[var(--ds-accent-purple)] transition-all cursor-pointer appearance-none"
                                >
                                    {t.footer.subscribeRoleOptions.map((option) => (
                                        <option key={option.value} value={option.value} className="bg-[#1a1a24] text-white">
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#64748b]">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Other Role Input */}
                        {role === 'other' && (
                            <input
                                type="text"
                                value={otherRole}
                                onChange={(e) => setOtherRole(e.target.value)}
                                placeholder={t.footer.subscribeOtherRolePlaceholder}
                                className="w-full h-12 px-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] text-[var(--ds-text-primary)] placeholder:text-[#64748b] text-sm focus:outline-none focus:border-[var(--ds-accent-purple)] transition-all animate-in slide-in-from-top-2 duration-200"
                            />
                        )}

                        {/* Interests Checkboxes - Grid Layout */}
                        <div className="space-y-2 pt-2">
                            <label className="text-xs font-medium text-[var(--ds-text-secondary)] ml-1">
                                {t.footer.subscribeInterestsLabel}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {t.footer.subscribeInterests.map((interest) => (
                                    <label
                                        key={interest.value}
                                        className={`relative flex items-center justify-center text-center px-4 py-3 rounded-xl text-sm cursor-pointer transition-all border ${interests.includes(interest.value)
                                            ? 'bg-[rgba(99,102,241,0.1)] border-[var(--ds-accent-purple)] text-white shadow-[0_0_15px_rgba(99,102,241,0.3)]'
                                            : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-[var(--ds-text-secondary)] hover:bg-[rgba(255,255,255,0.05)] hover:border-[rgba(255,255,255,0.15)]'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={interests.includes(interest.value)}
                                            onChange={() => handleInterestChange(interest.value)}
                                            className="sr-only"
                                        />
                                        {interests.includes(interest.value) && (
                                            <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[var(--ds-accent-purple)] shadow-[0_0_5px_var(--ds-accent-purple)]" />
                                        )}
                                        <span className={interests.includes(interest.value) ? 'font-medium' : ''}>
                                            {interest.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full h-12 mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[var(--ds-accent-purple)] to-[var(--ds-accent-indigo)] text-white font-semibold hover:shadow-[0_0_20px_rgba(99,102,241,0.5)] hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {isSubmitting ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    {t.footer.subscribeButton}
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
